import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {SearchResult} from '../_models/SearchResult';
import {CqCicloControllo} from '../_models/CqCicloControllo';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {HubConnection} from '@microsoft/signalr';
import {OrganizzazioniService} from './organizzazioni.service';
import {SaveMessage} from '../_helpers/save-message';
import {ControlloQualita} from '../_models/ControlloQualita';
import {CqCaratteristica} from '../_models/CqCaratteristica';
import {OrdineGroup} from '../_models/OrdineGroup';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';

@Injectable({providedIn: 'root'})
export class QualitaService {
  public statoControlli: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public esitoControlli: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public lastControlloCreated: BehaviorSubject<ControlloQualita> = new BehaviorSubject<ControlloQualita>(null);
  public lastControlloUpdated: BehaviorSubject<ControlloQualita> = new BehaviorSubject<ControlloQualita>(null);
  public lastControlloDeleted: BehaviorSubject<ControlloQualita> = new BehaviorSubject<ControlloQualita>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  private hubConnection: HubConnection;

  constructor(
    private http: HttpClient,
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage) {}

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}hubs/controlloqualita`, this.hubState);

    this.hubConnection.on('SendCreated', (controllo: ControlloQualita) => {
      if (controllo.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.entityCreated('controllo', controllo);
        this.lastControlloCreated.next(controllo);
      }
    });

    this.hubConnection.on('SendUpdated', (controllo: ControlloQualita) => {
      if (controllo.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.entityUpdated('controllo', controllo);
        this.lastControlloUpdated.next(controllo);
      }
    });

    this.hubConnection.on('SendDeleted', (controllo: ControlloQualita) => {
      if (controllo.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.entityDeleted('controllo', controllo);
        this.lastControlloDeleted.next(controllo);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  async getCaratteristicheById(orgId: number, idCaratteristica: number) {
    return firstValueFrom(
      this.http.get<SearchResult<CqCaratteristica>>(`${environment.localApiUrl}api/Qualita/CaratteristicheById/${orgId}/${idCaratteristica}`)
    );
  }

  async getControlliOfOdP(orgId: number, rifOdP: string, numFase: number) {
    return firstValueFrom(this.http.post<SearchResult<CqCicloControllo>>(`${environment.localApiUrl}api/Qualita/ControlliByRifOdP/${orgId}`,
      {page: 0, count: 0, search: rifOdP, numFase, sortField: 'IdSchedaControllo', sortAsc: true}));
  }

  async getControlliOfCodArt(orgId: number, codArt: string, numFase: number, idOrd?: number, varianteArt?: string) {
    return firstValueFrom(this.http.post<SearchResult<CqCicloControllo>>(`${environment.localApiUrl}api/Qualita/ControlliByCodArt/${orgId}`,
      {page: 0, count: 0, search: '', codArt, numFase, sortField: 'IdSchedaControllo', sortAsc: true, idOrd, varianteArt}));
  }

  async getControlli(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean,
                     codArt = '', esito?: number, inviato?: boolean, risorsaControllo = '',
                     idScheda?: number, idDocumento?: number, idRiga?: number,
                     startDate?: Date, endDate?: Date) {
    return firstValueFrom(this.http
      .post<SearchResult<ControlloQualita>>(`${environment.localApiUrl}api/Qualita/SearchControlli/${orgId}`,
        {search, page, count, sortField, sortAsc, codArt, esito, inviato, risorsaControllo,
          idScheda, idDocumento, idRiga, startDate, endDate}));
  }

  async createControlloAsync(controlloQualita: ControlloQualita) {
    return firstValueFrom(this.http.post<ControlloQualita>(`${environment.localApiUrl}api/Qualita/CreateControlloQualita`, controlloQualita));
  }

  // Accoda un Update su ControlloQualitaMqWorker (aggiornamento campi e/o reinvio a ESOLVER gestiti dal worker)
  async updateControlloQualitaAsync(controlloQualita: ControlloQualita) {
    return firstValueFrom(this.http.post<ControlloQualita>(`${environment.localApiUrl}api/Qualita/UpdateControlloQualita`, controlloQualita));
  }

  // Logica condivisa di (re)invio a ESOLVER: azzera il riferimento (idRigaSchedaControllo) e la data come
  // segnale e accoda l'Update; il worker, trovando idRigaSchedaControllo == 0, esegue CreateSchedaRiga.
  async reinviaControlloEsolver(controllo: ControlloQualita): Promise<void> {
    controllo.idRigaSchedaControllo = 0;
    controllo.dataRegistrazione = null;
    try {
      await this.updateControlloQualitaAsync(controllo);
    } catch {
      this.saveMessage.messageError('Errore durante il reinvio del controllo a ESOLVER.');
    }
  }

  async sendControlloAsync(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, controlloQualita: ControlloQualita, userName: string) {
    return firstValueFrom(this.http.post<ControlloQualita>(`${environment.localApiUrl}api/Qualita/SendControlloQualita`,
      {orgId, dataEvento, ordineGroup, controlloQualita, userName}));
  }

  async deleteControlloQualita(orgId: number, idSchedaControllo: number, idRigaCicloControllo: number, controlloId: number) {
    return firstValueFrom(this.http
      .get<ControlloQualita>(`${environment.localApiUrl}api/Qualita/DeleteControlloQualita/${orgId}/${idSchedaControllo}/${idRigaCicloControllo}/${controlloId}`));
  }

  getIdOrdine(rifOrdine: string) {
    if (rifOrdine.length < 13) {
      return {idDocumento: 0, idRiga: 0};
    }
    return {idDocumento: parseInt(rifOrdine.substring(0, 9), 10), idRiga: parseInt(rifOrdine.substring(9, 13), 10)};
  }
}
